import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { useSnackbar } from 'notistack';
import imputableService, { ImputableCRUD } from '../../services/imputableService';

const defaultForm: ImputableCRUD = {
  codImputable: '',
  nombre: '',
  nivel: 0,
  imputable: 'S',
  tipo: 'A',
  activo: 'S',
  codImputablePadre: '',
  descripcion: ''
};

const tiposImputable = [
  { value: 'A', label: 'Activo' },
  { value: 'P', label: 'Pasivo' },
  { value: 'R', label: 'Resultado' },
  { value: 'O', label: 'Orden' }
];

const ImputableForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codImputable } = useParams();
  const [formData, setFormData] = useState<ImputableCRUD>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  const loadImputable = useCallback(async () => {
    if (!codImputable || codImputable === 'nuevo') return;
    try {
      setLoading(true);
      const imputable = await imputableService.getImputable(codImputable);
      if (imputable) {
        setFormData({ ...defaultForm, ...imputable });
      }
    } catch (error) {
      enqueueSnackbar('Error al cargar el imputable', { variant: 'error' });
      navigate('/contabilidad/imputables');
    } finally {
      setLoading(false);
    }
  }, [codImputable, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codImputable && codImputable !== 'nuevo') loadImputable();
  }, [codImputable, loadImputable]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nivel') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSwitchChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [name]: e.target.checked ? 'S' : 'N' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.codImputable.trim()) {
        enqueueSnackbar('El código del imputable es requerido', { variant: 'warning' });
        return;
      }

      if (!formData.nombre.trim()) {
        enqueueSnackbar('El nombre del imputable es requerido', { variant: 'warning' });
        return;
      }

      if (codImputable && codImputable !== 'nuevo') {
        await imputableService.updateImputable(codImputable, formData);
      } else {
        await imputableService.createImputable(formData);
      }

      enqueueSnackbar('Imputable guardado exitosamente', { variant: 'success' });
      navigate('/contabilidad/imputables');
    } catch (error: any) {
      console.error('Error al guardar imputable:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el imputable', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {codImputable && codImputable !== 'nuevo' ? 'Editar Imputable' : 'Nuevo Imputable'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Configuración" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="codImputable" 
                    label="Código" 
                    value={formData.codImputable} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(codImputable && codImputable !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField 
                    name="nombre" 
                    label="Nombre" 
                    value={formData.nombre} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="descripcion" 
                    label="Descripción" 
                    value={formData.descripcion || ''} 
                    onChange={handleChange} 
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="nivel" 
                    label="Nivel" 
                    type="number"
                    value={formData.nivel} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      name="tipo"
                      value={formData.tipo}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                      label="Tipo"
                      sx={{ borderRadius: 2 }}
                    >
                      {tiposImputable.map(tipo => (
                        <MenuItem key={tipo.value} value={tipo.value}>{tipo.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="codImputablePadre" 
                    label="Código Padre" 
                    value={formData.codImputablePadre || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.imputable === 'S'} onChange={handleSwitchChange('imputable')} />}
                    label="Es Imputable"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.activo === 'S'} onChange={handleSwitchChange('activo')} />}
                    label="Activo"
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/contabilidad/imputables')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ImputableForm;
